
import { GoogleGenAI, Modality, Type } from "@google/genai";
import { Incident, ThreatLevel, GeoLocation } from "../types";

// ── Model config — free-tier compatible ───────────────────────────────────────
// gemini-2.0-flash: free tier, supports Google Search grounding
// gemini-1.5-flash: free tier fallback
const MODEL_FAST   = 'gemini-2.0-flash';
const MODEL_SEARCH = 'gemini-2.0-flash';   // grounded search
const MODEL_TTS    = 'gemini-2.0-flash';   // no free TTS model — falls back to browser

const ai = new GoogleGenAI({ apiKey: import.meta.env.VITE_GEMINI_API_KEY || '' });

let lastTTSCall = 0;
const TTS_COOLDOWN = 15000;

function makeAI(apiKey?: string) {
  return apiKey ? new GoogleGenAI({ apiKey }) : ai;
}

async function retry<T>(fn: () => Promise<T>, retries = 2, delay = 3000): Promise<T> {
  try {
    return await fn();
  } catch (error: any) {
    if (error?.message?.includes('429') || error?.status === 429) {
      console.warn('[Gemini] Rate limited (429)');
      throw new Error('RATE_LIMIT_EXHAUSTED');
    }
    if (retries > 0 && (error?.status === 500 || error?.message?.includes('500') || error?.message?.includes('xhr error'))) {
      console.warn(`[Gemini] Transient error, retrying... (${retries} left)`);
      await new Promise(r => setTimeout(r, delay));
      return retry(fn, retries - 1, delay * 2);
    }
    throw error;
  }
}

// ── Nominatim geocoder (free, no API key) ─────────────────────────────────────
async function nominatimGeocode(address: string): Promise<{ lat: number; lng: number } | null> {
  try {
    const q = encodeURIComponent(address);
    const url = `https://nominatim.openstreetmap.org/search?q=${q}&format=json&limit=1&countrycodes=us`;
    const res = await fetch(url, { headers: { 'User-Agent': 'TucsonStreets/1.0' } });
    const data = await res.json();
    if (data?.[0]) return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
  } catch (e) {
    console.warn('[Nominatim] geocode failed for:', address, e);
  }
  return null;
}

// ── 1. Live threat intel — Google Search grounded ─────────────────────────────
export async function fetchLiveThreats(location: GeoLocation, apiKey?: string): Promise<Incident[]> {
  const task = async () => {
    const response = await makeAI(apiKey).models.generateContent({
      model: MODEL_SEARCH,
      contents: `You are a public safety analyst for Tucson, AZ. Use Google Search to find ACTIVE police and emergency incidents near coordinates ${location.lat}, ${location.lng} reported in the last 35 minutes.

Return ONLY a JSON array (no markdown). Each item:
{"id":"1","type":"ASSAULT","description":"...","address":"500 N Stone Ave, Tucson AZ","threatLevel":"HIGH","source":"DISPATCH","freshnessMinutes":5}

Fields: id(string), type(string), description(string), address(string — full street address or intersection in Tucson AZ), threatLevel(LOW|MEDIUM|HIGH|CRITICAL), source(RADIO|DISPATCH|USER_REPORT|CRIME_RADAR), freshnessMinutes(integer).

If nothing found, return [].`,
      config: {
        tools: [{ googleSearch: {} }],
      }
    });

    const text = response.text || '[]';
    const jsonMatch = text.match(/\[[\s\S]*\]/);
    const rawItems: any[] = jsonMatch ? JSON.parse(jsonMatch[0]) : [];

    // Geocode each incident address
    const incidents = await Promise.all(
      rawItems.map(async (item: any) => {
        const { address, freshnessMinutes, ...rest } = item;
        let coords = address ? await nominatimGeocode(address) : null;
        if (!coords) {
          coords = {
            lat: location.lat + (Math.random() - 0.5) * 0.04,
            lng: location.lng + (Math.random() - 0.5) * 0.04,
          };
        }
        return {
          ...rest,
          freshnessMinutes: freshnessMinutes ?? 0,
          location: coords,
          timestamp: Date.now() - (freshnessMinutes ?? 0) * 60000,
        } as Incident;
      })
    );
    return incidents;
  };

  try {
    return await retry(task);
  } catch (error: any) {
    if (error?.message === 'RATE_LIMIT_EXHAUSTED') throw error;
    console.error('[Gemini] Threat fetch error:', error);
    return [];
  }
}

// ── 2. Area safety analysis ────────────────────────────────────────────────────
export async function analyzeIncidentVicinity(location: GeoLocation, apiKey?: string): Promise<string> {
  const task = async () => {
    const response = await makeAI(apiKey).models.generateContent({
      model: MODEL_SEARCH,
      contents: `Use Google Search to analyze public safety near lat ${location.lat}, lng ${location.lng} in Tucson AZ. Report any current roadblocks, high police presence, or active incidents within 500 meters. Be concise, 2-3 sentences.`,
      config: { tools: [{ googleSearch: {} }] },
    });
    return response.text || 'Vicinity analysis unavailable.';
  };

  try {
    return await retry(task);
  } catch (error: any) {
    if (error?.message === 'RATE_LIMIT_EXHAUSTED') throw error;
    console.error('[Gemini] Vicinity analysis error:', error);
    return 'Error analyzing vicinity.';
  }
}

// ── 3. TTS — browser SpeechSynthesis (free, no API needed) ────────────────────
export async function playTacticalAlert(text: string, apiKey?: string) {
  const now = Date.now();
  if (now - lastTTSCall < TTS_COOLDOWN) return;
  lastTTSCall = now;

  // Try Gemini TTS first (only works on paid accounts)
  if (apiKey) {
    try {
      const response = await makeAI(apiKey).models.generateContent({
        model: 'gemini-2.5-flash-preview-tts',
        contents: [{ parts: [{ text: `Warning: ${text}` }] }],
        config: {
          responseModalities: [Modality.AUDIO],
          speechConfig: {
            voiceConfig: { prebuiltVoiceConfig: { voiceName: 'Puck' } },
          },
        },
      });
      const base64Audio = response.candidates?.[0]?.content?.parts?.[0]?.inlineData?.data;
      if (base64Audio) {
        const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 24000 });
        const decodedData = decode(base64Audio);
        const audioBuffer = await decodeAudioData(decodedData, audioCtx, 24000, 1);
        const source = audioCtx.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(audioCtx.destination);
        source.start();
        return;
      }
    } catch(e) {
      console.warn('[Gemini TTS] Falling back to browser TTS:', e);
    }
  }

  // Free fallback: browser SpeechSynthesis
  if ('speechSynthesis' in window) {
    window.speechSynthesis.cancel();
    const utter = new SpeechSynthesisUtterance(`Warning: ${text}`);
    utter.rate = 0.95;
    utter.pitch = 0.85;
    utter.volume = 1;
    // Try to pick a deep/military voice
    const voices = window.speechSynthesis.getVoices();
    const preferred = voices.find(v => /David|Alex|Daniel|Google UK|en-US/i.test(v.name));
    if (preferred) utter.voice = preferred;
    window.speechSynthesis.speak(utter);
  }
}

// ── 4. Summarize Dispatch ─────────────────────────────────────────────────────
export async function summarizeDispatch(dispatchText: string, apiKey?: string): Promise<string> {
  const task = async () => {
    const response = await makeAI(apiKey).models.generateContent({
      model: MODEL_FAST,
      contents: `Summarize this police radio call in plain English, max 40 words, no jargon: "${dispatchText}"`,
      config: { temperature: 0.3, maxOutputTokens: 80 },
    });
    return response.text || 'Summary unavailable.';
  };

  try {
    return await retry(task);
  } catch (error: any) {
    if (error?.message === 'RATE_LIMIT_EXHAUSTED') throw error;
    console.error('[Gemini] Summarize error:', error);
    return 'Error generating summary.';
  }
}

// ── 5. Audio Transcription ────────────────────────────────────────────────────
export async function transcribeAudio(base64Audio: string, mimeType: string, apiKey?: string): Promise<string> {
  const task = async () => {
    const response = await makeAI(apiKey).models.generateContent({
      model: MODEL_FAST,
      contents: [
        { inlineData: { mimeType, data: base64Audio } },
        { text: 'Transcribe this audio accurately. If police/emergency radio, preserve tactical terminology.' }
      ]
    });
    return response.text || 'Transcription unavailable.';
  };

  try {
    return await retry(task);
  } catch (error: any) {
    if (error?.message === 'RATE_LIMIT_EXHAUSTED') throw error;
    console.error('[Gemini] Transcription error:', error);
    return 'Error transcribing audio.';
  }
}

// ── 6. Verify Incident Location ───────────────────────────────────────────────
export async function verifyIncidentLocation(incident: Incident, apiKey?: string): Promise<{ text: string; sources: any[] }> {
  const task = async () => {
    const response = await makeAI(apiKey).models.generateContent({
      model: MODEL_SEARCH,
      contents: `Verify this incident location using Google Search: "${incident.description}" at lat ${incident.location.lat}, lng ${incident.location.lng} in Tucson AZ. Is this address/location plausible? Reply: Accurate / Inaccurate / Unsure — then one sentence why.`,
      config: { tools: [{ googleSearch: {} }] },
    });
    const sources = response.candidates?.[0]?.groundingMetadata?.groundingChunks || [];
    return { text: response.text || 'Verification unavailable.', sources };
  };

  try {
    return await retry(task);
  } catch (error: any) {
    if (error?.message === 'RATE_LIMIT_EXHAUSTED') throw error;
    console.error('[Gemini] Verification error:', error);
    return { text: 'Error verifying location.', sources: [] };
  }
}

// ── 7. Geocode Address ────────────────────────────────────────────────────────
export async function geocodeAddress(address: string, apiKey?: string): Promise<{ lat: number; lng: number } | null> {
  // Primary: Nominatim (free, accurate)
  const nominatimResult = await nominatimGeocode(address);
  if (nominatimResult) return nominatimResult;

  // Fallback: ask Gemini with search grounding
  try {
    const task = async () => {
      const response = await makeAI(apiKey).models.generateContent({
        model: MODEL_FAST,
        contents: `Return ONLY a JSON object with the geocoordinates for this address in Tucson AZ, no prose:
{"lat": <number>, "lng": <number>}
Address: "${address}"
If not found, return: {"lat": null, "lng": null}`,
        config: {
          responseMimeType: 'application/json',
          responseSchema: {
            type: Type.OBJECT,
            properties: {
              lat: { type: Type.NUMBER },
              lng: { type: Type.NUMBER },
            },
            required: ['lat', 'lng'],
          }
        },
      });
      const data = JSON.parse(response.text || '{}');
      return (data.lat && data.lng) ? { lat: data.lat, lng: data.lng } : null;
    };
    return await retry(task);
  } catch (error: any) {
    if (error?.message === 'RATE_LIMIT_EXHAUSTED') throw error;
    console.error('[Gemini] Geocode fallback error:', error);
    return null;
  }
}

// ── 8. Wild West concept image (paid only — graceful no-op on free) ───────────
export async function generateWildWestConcept(locationName: string, apiKey?: string, customPrompt?: string): Promise<string | null> {
  if (!apiKey) {
    console.warn('[Gemini] Image generation requires a personal API key (Ghost tier)');
    return null;
  }
  try {
    const basePrompt = `A highly detailed, realistic 3D top-down map view of ${locationName} in Tucson, Arizona. Expressive styling: the most unique wild west cyber-cowboy city in the world, inspired by the "Noble Glitch" aesthetic. Make the map a 3D terrain with detailed city district effects. Ensure it aligns with Google Maps street layouts but transformed into a cyberpunk western. Dusty desert terrain, neon-lit saloons, high-tech dirt roads, holographic cacti. Include a cybernetic cowboy figure ("Noble Glitch") and his loyal K9 companion ("Arya"). High fidelity, photorealistic, Unreal Engine 5 style.`;
    const finalPrompt = customPrompt ? `${basePrompt} Additional: ${customPrompt}` : basePrompt;

    const response = await makeAI(apiKey).models.generateContent({
      model: 'gemini-2.0-flash-preview-image-generation',
      contents: finalPrompt,
      config: { responseModalities: ['TEXT', 'IMAGE'] },
    });

    if (response.candidates?.[0]?.content?.parts) {
      for (const part of response.candidates[0].content.parts) {
        if ((part as any).inlineData) {
          const p = part as any;
          return `data:${p.inlineData.mimeType};base64,${p.inlineData.data}`;
        }
      }
    }
    return null;
  } catch (e: any) {
    console.warn('[Gemini] Image generation error (may need paid plan):', e?.message);
    return null;
  }
}

// ── Audio helpers ─────────────────────────────────────────────────────────────
function decode(base64: string) {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

async function decodeAudioData(data: Uint8Array, ctx: AudioContext, sampleRate: number, numChannels: number): Promise<AudioBuffer> {
  const dataInt16 = new Int16Array(data.buffer);
  const frameCount = dataInt16.length / numChannels;
  const buffer = ctx.createBuffer(numChannels, frameCount, sampleRate);
  for (let channel = 0; channel < numChannels; channel++) {
    const channelData = buffer.getChannelData(channel);
    for (let i = 0; i < frameCount; i++) {
      channelData[i] = dataInt16[i * numChannels + channel] / 32768.0;
    }
  }
  return buffer;
}
